import { useCallback } from 'react'
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage'

/**
 * Descriptor stored on the draft for a locally-uploaded invitation
 * attachment. The bytes live in Firebase Storage; the draft only carries
 * what the read side needs to render a chip and resolve a download URL.
 */
export interface HuntAttachmentFile {
  fileName: string
  storagePath: string
  mimeType: string | null
  sizeBytes: number | null
  /**
   * The download URL as `getDownloadURL()` returned it, token included.
   *
   * Captured here because this is the only moment it can be: the token is object
   * metadata minted by the Storage SDK on upload, so it cannot be reconstructed
   * from `storagePath` later or on the server. The API mirrors it onto the hunt
   * plan as `attachments`, which is what the mobile invitation reads — a hunt
   * planned on the web previously showed no attachments there at all.
   *
   * Do NOT strip the token before persisting. The mobile reader fetches this URL
   * with a plain unauthenticated GET, and the token is exactly what grants that
   * request access to the single object; a tokenless URL is refused by the
   * bucket rules. Stripping it is a mistake this codebase has already shipped
   * once, inherited from a legacy bucket whose rules allowed anonymous reads.
   *
   * Optional so a draft saved before the field existed still loads; the web's own
   * read path resolves from `storagePath` and does not depend on it.
   */
  downloadUrl?: string | null
}

/**
 * MIME / extension whitelist the file picker should advertise via `accept`
 * and that this hook validates client-side before uploading. PDF + Excel
 * (xls/xlsx) + Word (doc/docx) are the only formats hunt leaders attach
 * to invitations today; anything else is rejected so users don't
 * accidentally upload arbitrary files that downstream readers can't open.
 */
export const ATTACHMENT_ACCEPT = '.pdf,.xls,.xlsx,.doc,.docx'

const ALLOWED_EXTENSIONS = new Set(['pdf', 'xls', 'xlsx', 'doc', 'docx'])
const MAX_BYTES = 25 * 1024 * 1024 // 25 MB — keeps Firestore + email attachments sensible

/**
 * Returns an async uploader for a single file picked via the OS dialog on
 * Step 4 (Invitation). Uploads to
 * `huntPlans/{draftId}/attachments/{uuid}.{ext}` and returns the descriptor
 * to add to the draft's `attachmentFiles` list.
 *
 * Bound to a specific draftId — callers must guarantee non-null. In the
 * wizard this holds by the time the user reaches Step 4 because every
 * Continue persists the draft.
 */
export function useUploadHuntAttachment(draftId: string | null) {
  return useCallback(
    async (file: File): Promise<HuntAttachmentFile> => {
      if (!draftId) {
        throw new Error(
          'useUploadHuntAttachment: draftId is null. Save the draft before uploading attachments.',
        )
      }

      const extension = pickExtension(file.name)
      if (!extension || !ALLOWED_EXTENSIONS.has(extension)) {
        throw new Error(
          `Unsupported file type. Allowed: ${[...ALLOWED_EXTENSIONS].join(', ').toUpperCase()}.`,
        )
      }
      if (file.size > MAX_BYTES) {
        throw new Error(`File is too large. Max ${Math.floor(MAX_BYTES / (1024 * 1024))} MB.`)
      }

      const objectName = `${cryptoRandomId()}.${extension}`
      const path = `huntPlans/${draftId}/attachments/${objectName}`

      const storage = getStorage()
      const objectRef = ref(storage, path)
      await uploadBytes(objectRef, file, {
        contentType: file.type || undefined,
        // Hint browsers to download with the original filename when the user
        // clicks the chip rather than rendering inline (PDFs would otherwise
        // open in-tab named after the random UUID).
        customMetadata: { originalName: file.name },
      })

      return {
        fileName: file.name,
        storagePath: path,
        mimeType: file.type || null,
        sizeBytes: file.size,
        // Deliberately unguarded. If this throws, the whole upload fails and the
        // user retries — which is right. The tempting "fix" is to catch it and fall
        // back to downloadUrl: null, but that yields an attachment the web shows and
        // the app silently does not, which is exactly the bug this field exists to
        // close, now invisible to the person who just uploaded it. Fail loudly; the
        // orphaned Storage object is the cheaper problem.
        downloadUrl: await getDownloadURL(objectRef),
      }
    },
    [draftId],
  )
}

function pickExtension(fileName: string): string | null {
  const dot = fileName.lastIndexOf('.')
  if (dot <= 0 || dot >= fileName.length - 1) return null
  return fileName.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') || null
}

function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '')
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}
